import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlignRight, Phone, PackageSearch, Facebook, Instagram, Twitter, Youtube, Linkedin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { startCacheVersionWatch } from "@/lib/cacheVersion";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { getIcon } from "@/lib/iconRegistry";

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, twitter: Twitter, youtube: Youtube, linkedin: Linkedin };

const SETTINGS_CACHE_KEY = "gu_site_settings_v1";
const readCachedSettings = () => {
  try { return JSON.parse(localStorage.getItem(SETTINGS_CACHE_KEY)); } catch { return null; }
};

export default function Header() {
  const navigate = useNavigate();
  const [trackNumber, setTrackNumber] = useState("");
  const [trackOpen, setTrackOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settings, setSettings] = useState(readCachedSettings);
  const [services, setServices] = useState([]);
  const [pageVisibility, setPageVisibility] = useState({ packages: true, vacancies: true, visas: true });

  useEffect(() => {
    api.get("/site-settings").then((res) => {
      setSettings(res.data);
      try { localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(res.data)); } catch (e) { /* storage full */ }
    }).catch(() => {});
    const cacheIv = startCacheVersionWatch();
    api.get("/services").then((res) => setServices(res.data)).catch(() => {});
    api.get("/page-visibility").then((res) => setPageVisibility(res.data)).catch(() => {});
    return () => clearInterval(cacheIv);
  }, []);

  const navLinks = settings?.nav_links || [{ label: "Blog", path: "/blog" }, { label: "Contact", path: "/contact" }];

  const handleTrack = (e) => {
    e.preventDefault();
    if (!trackNumber.trim()) return;
    setTrackOpen(false);
    navigate(`/track?number=${trackNumber.trim().toUpperCase()}`);
  };

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-[#E5E1DA] shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          {settings?.logo_url ? (
            <img src={settings.logo_url} alt="Gulfudaan" data-testid="header-logo-image" className="h-10 sm:h-12 w-auto object-contain" />
          ) : (
            <span className="font-heading text-2xl sm:text-3xl font-bold text-[#1A1A1A]">
              Gulf<span className="text-[var(--brand-primary)]">udaan</span>
            </span>
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          <Link to="/" data-testid="nav-home" className="text-sm font-medium whitespace-nowrap text-[var(--brand-secondary)] hover:text-[var(--brand-primary)] transition-colors">Home</Link>
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="nav-services-trigger" className="text-sm font-medium text-[var(--brand-secondary)] hover:text-[var(--brand-primary)] transition-colors outline-none">
              Services
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64 bg-white">
              {services.map((s) => (
                <DropdownMenuItem key={s.slug} asChild>
                  <Link data-testid={`nav-service-${s.slug}`} to={`/services/${s.slug}`} className="cursor-pointer">{s.name}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {pageVisibility.packages && (
            <Link to="/packages" data-testid="nav-packages" className="text-sm font-medium text-[var(--brand-secondary)] hover:text-[var(--brand-primary)] transition-colors">Packages</Link>
          )}
          {pageVisibility.visas && (
            <Link to="/visas" data-testid="nav-visas" className="text-sm font-medium text-[var(--brand-secondary)] hover:text-[var(--brand-primary)] transition-colors">Visas</Link>
          )}
          {pageVisibility.vacancies && (
            <Link to="/vacancies" data-testid="nav-vacancies" className="text-sm font-medium whitespace-nowrap text-[var(--brand-secondary)] hover:text-[var(--brand-primary)] transition-colors">Vacancies</Link>
          )}
          {navLinks.map((l) => (
            <Link key={l.path} to={l.path} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm font-medium whitespace-nowrap text-[var(--brand-secondary)] hover:text-[var(--brand-primary)] transition-colors">{l.label}</Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <div className="hidden xl:flex items-center gap-2 mr-2">
            {settings?.social_links && Object.entries(settings.social_links).map(([key, url]) => {
              const Icon = SOCIAL_ICONS[key];
              if (!Icon || !url) return null;
              return (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer" data-testid={`social-icon-${key}`} className="text-[var(--brand-secondary)] hover:text-[var(--brand-primary)] transition-colors">
                  <Icon size={18} />
                </a>
              );
            })}
          </div>
          <Dialog open={trackOpen} onOpenChange={setTrackOpen}>
            <DialogTrigger asChild>
              <Button data-testid="track-application-btn-desktop" variant="outline" className="border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[color-mix(in_oklab,var(--brand-primary),transparent_90%)]">
                <PackageSearch size={16} className="mr-2" /> Track Application
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Track Your Application</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleTrack} className="flex flex-col gap-4 pt-2">
                <Input data-testid="track-number-input" placeholder="Enter Application Number e.g. GU2607123456" value={trackNumber} onChange={(e) => setTrackNumber(e.target.value)} />
                <Button data-testid="track-submit-btn" type="submit" className="bg-[var(--brand-primary)] hover:bg-[#9E855A] text-white">Track Status</Button>
              </form>
            </DialogContent>
          </Dialog>
          <a href={`tel:${settings?.phone || "+917314344673"}`} data-testid="header-call-btn">
            <Button className="bg-[var(--brand-primary)] hover:bg-[#9E855A] text-white"><Phone size={16} className="mr-2" /> Call Now</Button>
          </a>
        </div>

        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <div className="flex items-center gap-1.5 lg:hidden">
            <Dialog open={trackOpen} onOpenChange={setTrackOpen}>
              <DialogTrigger asChild>
                <Button data-testid="track-application-btn-mobile-icon" variant="ghost" size="icon"
                  className="w-9 h-9 text-[var(--brand-primary)] hover:opacity-70">
                  <PackageSearch size={20} />
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader><DialogTitle className="font-heading text-2xl">Track Your Application</DialogTitle></DialogHeader>
                <form onSubmit={handleTrack} className="flex flex-col gap-4 pt-2">
                  <Input data-testid="track-number-input-mobile" placeholder="Enter Application Number" value={trackNumber} onChange={(e) => setTrackNumber(e.target.value)} />
                  <Button type="submit" className="bg-[var(--brand-primary)] text-white">Track Status</Button>
                </form>
              </DialogContent>
            </Dialog>
            <a href="tel:+917314344673" data-testid="call-icon-mobile"
              className="w-9 h-9 flex items-center justify-center text-[var(--brand-primary)] hover:opacity-70">
              <Phone size={18} />
            </a>
            <a href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || "917314344673"}?text=${encodeURIComponent("Hi Gulfudaan, I need help with my application.")}`}
              target="_blank" rel="noopener noreferrer" data-testid="whatsapp-icon-mobile"
              className="w-9 h-9 flex items-center justify-center hover:opacity-70">
              <WhatsAppIcon size={21} className="text-[#25D366]" />
            </a>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-btn" variant="ghost" size="icon"
                className="w-10 h-10 ml-0.5 rounded-full bg-[color-mix(in_oklab,var(--brand-primary),transparent_88%)] text-[var(--brand-primary)] hover:bg-[color-mix(in_oklab,var(--brand-primary),transparent_78%)]">
                <AlignRight size={22} />
              </Button>
            </SheetTrigger>
          </div>
          <SheetContent side="right" className="bg-white w-[86vw] max-w-sm p-0 flex flex-col gap-0">
            <div className="flex-1 overflow-y-auto">
              <div className="px-6 pt-8 pb-5 border-b border-[#E5E1DA] bg-[#FDFCFB]">
                {settings?.logo_url ? (
                  <img src={settings.logo_url} alt="Gulfudaan" className="h-9 w-auto object-contain" />
                ) : (
                  <span className="font-heading text-2xl font-bold text-[#1A1A1A]">
                    Gulf<span className="text-[var(--brand-primary)]">udaan</span>
                  </span>
                )}
                <p className="text-xs text-[var(--brand-secondary)] mt-2">Fast, secure Gulf visa &amp; travel services.</p>
              </div>

              <div className="px-5 py-5">
                <Link to="/" data-testid="mobile-nav-home" onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-between text-base font-semibold text-[#1A1A1A] py-2.5 border-b border-[#E5E1DA]">
                  Home <ArrowRight size={15} className="text-[var(--brand-primary)]" />
                </Link>

                <p className="text-xs uppercase tracking-wider text-[var(--brand-primary)] font-bold mt-5 mb-3">Popular Services</p>
                <div className="grid grid-cols-2 gap-2.5">
                  {services.slice(0, 6).map((s) => {
                    const Icon = getIcon(s.icon);
                    return (
                      <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setMobileMenuOpen(false)}
                        data-testid={`mobile-nav-service-${s.slug}`}
                        className="flex flex-col items-center text-center gap-1.5 p-3 rounded-xl border border-[#E5E1DA] hover:border-[var(--brand-primary)] hover:bg-[color-mix(in_oklab,var(--brand-primary),transparent_94%)] transition-colors">
                        <Icon size={20} className="text-[var(--brand-primary)]" />
                        <span className="text-[11px] font-medium leading-tight text-[#1A1A1A]">{s.name}</span>
                      </Link>
                    );
                  })}
                </div>
                {services.length > 6 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger data-testid="mobile-nav-more-services" className="w-full text-xs font-medium text-[var(--brand-primary)] mt-2.5 text-left outline-none">
                      + {services.length - 6} more services
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-64 bg-white">
                      {services.slice(6).map((s) => (
                        <DropdownMenuItem key={s.slug} asChild>
                          <Link to={`/services/${s.slug}`} onClick={() => setMobileMenuOpen(false)} className="cursor-pointer">{s.name}</Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                <div className="mt-6 space-y-1">
                  {pageVisibility.packages && (
                    <Link to="/packages" data-testid="mobile-nav-packages" onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-between text-sm font-medium text-[#1A1A1A] py-2.5 border-b border-[#E5E1DA]">
                      Umrah &amp; Tour Packages <ArrowRight size={14} className="text-[var(--brand-secondary)]" />
                    </Link>
                  )}
                  {pageVisibility.visas && (
                    <Link to="/visas" data-testid="mobile-nav-visas" onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-between text-sm font-medium text-[#1A1A1A] py-2.5 border-b border-[#E5E1DA]">
                      Umrah &amp; Visit Visas <ArrowRight size={14} className="text-[var(--brand-secondary)]" />
                    </Link>
                  )}
                  {pageVisibility.vacancies && (
                    <Link to="/vacancies" data-testid="mobile-nav-vacancies" onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-between text-sm font-medium text-[#1A1A1A] py-2.5 border-b border-[#E5E1DA]">
                      Work Visa Vacancies <ArrowRight size={14} className="text-[var(--brand-secondary)]" />
                    </Link>
                  )}
                  {navLinks.map((l) => (
                    <Link key={l.path} to={l.path} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-between text-sm font-medium text-[#1A1A1A] py-2.5 border-b border-[#E5E1DA] last:border-0">
                      {l.label} <ArrowRight size={14} className="text-[var(--brand-secondary)]" />
                    </Link>
                  ))}
                </div>

                {settings?.social_links && Object.values(settings.social_links).some(Boolean) && (
                  <div className="flex items-center gap-3 mt-6">
                    {Object.entries(settings.social_links).map(([key, url]) => {
                      const Icon = SOCIAL_ICONS[key];
                      if (!Icon || !url) return null;
                      return (
                        <a key={key} href={url} target="_blank" rel="noopener noreferrer" data-testid={`mobile-social-icon-${key}`}
                          className="w-8 h-8 rounded-full bg-[#FDFCFB] border border-[#E5E1DA] flex items-center justify-center text-[var(--brand-secondary)] hover:text-[var(--brand-primary)]">
                          <Icon size={14} />
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="shrink-0 px-4 pt-4 pb-20 border-t border-[#E5E1DA] bg-white space-y-2">
              <Button data-testid="mobile-nav-track-btn" onClick={() => { setMobileMenuOpen(false); setTrackOpen(true); }}
                variant="outline" className="w-full border-[var(--brand-primary)] text-[var(--brand-primary)]">
                <PackageSearch size={16} className="mr-2" /> Track Application
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <a href={`tel:${settings?.phone || "+917314344673"}`} data-testid="mobile-nav-call-btn" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-[var(--brand-primary)] hover:bg-[#9E855A] text-white"><Phone size={15} className="mr-1.5" /> Call Now</Button>
                </a>
                <a href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || "917314344673"}?text=${encodeURIComponent("Hi Gulfudaan, I need help with my application.")}`}
                  target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-whatsapp-btn" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-[#25D366] hover:bg-[#1ea952] text-white"><WhatsAppIcon size={15} className="mr-1.5 text-white" /> WhatsApp</Button>
                </a>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
